import { toast } from 'sonner';
import { ApiError } from '@/lib/api/apiClient';

export interface ChatErrorHandlerOptions {
  onUnauthorized?: () => void;
  onEmailNotVerified?: () => void;
  /** Sender hasn't verified their photo / added a social link yet — see ChatController::assertCanMessage. */
  onProfileIncomplete?: () => void;
}

function extractStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status;
  if (err && typeof err === 'object' && 'status' in err) {
    const s = (err as { status: unknown }).status;
    if (typeof s === 'number') return s;
  }
  return undefined;
}

function extractBody(err: unknown): Record<string, unknown> {
  if (err instanceof ApiError && err.data && typeof err.data === 'object') {
    return err.data as Record<string, unknown>;
  }
  if (err && typeof err === 'object' && 'body' in err) {
    const b = (err as { body: unknown }).body;
    if (b && typeof b === 'object') return b as Record<string, unknown>;
  }
  if (err instanceof Error) return { message: err.message };
  return { message: 'Something went wrong' };
}

function extractMessage(body: Record<string, unknown>): string {
  if (typeof body.message === 'string') {
    const stripped = body.message.replace(/<[^>]+>/g, '').trim();
    return stripped || body.message;
  }
  return 'Something went wrong';
}

export function handleChatError(err: unknown, options?: ChatErrorHandlerOptions): void {
  const status = extractStatus(err);
  const body = extractBody(err);
  const message = extractMessage(body);

  if (status === 401 || message === 'Unauthenticated.') {
    toast.error('Please log in to continue.');
    options?.onUnauthorized?.();
    return;
  }

  if (status === 423 || body.error === 'email_not_verified') {
    toast.error('Please verify your email to use chat.');
    options?.onEmailNotVerified?.();
    return;
  }

  if (status === 403 && body.code === 'profile_incomplete') {
    toast.error(message || 'Verify your photo and add a social link before messaging.');
    options?.onProfileIncomplete?.();
    return;
  }

  if (status === 403) {
    toast.error(message || 'This action is not allowed.');
    return;
  }

  if (status === 404) {
    toast.error(message || 'Not found.');
    return;
  }

  if (status === 422) {
    toast.error(message);
    return;
  }

  toast.error(message);
}
